import { useEffect, useState } from 'react';
import { Box, Flex, Heading, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { MdClose, MdPhonelinkSetup } from 'react-icons/md';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import {
  ANDROID_PWA_REPAIR_STEPS,
  ANDROID_PWA_REPAIR_SUMMARY,
  ANDROID_PWA_REPAIR_TITLE,
} from '../constants/pwaSupport';
import { MOBILE_TAB_BAR_HEIGHT } from './layout/MobileBottomNav';
import { getRouteChrome } from './layout/routeChrome';
import { devQuery, IS_DEV_BUILD } from '../constants/devControls';

const SHOW_DELAY_MS = devQuery.pwaPreview() ? 0 : IS_DEV_BUILD ? 500 : 2500;

/**
 * Shown in a normal Android Chrome tab when the WebAPK is still installed but
 * the user is not in standalone mode — the usual fallback after the system
 * “requires Chrome” dialog blocks the home-screen icon.
 */
export function PwaAndroidRepairBanner() {
  const { needsAndroidRepair, isAndroidRepairSnoozed, dismissAndroidRepair } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  const hasBottomNav = getRouteChrome(location.pathname).showMobileTabs;
  const bottomOffset = hasBottomNav
    ? `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 8px)`
    : 'calc(env(safe-area-inset-bottom, 0px) + 8px)';

  useEffect(() => {
    if (!needsAndroidRepair || isAndroidRepairSnoozed) return;
    const t = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [needsAndroidRepair, isAndroidRepairSnoozed]);

  if (!visible || !needsAndroidRepair || isAndroidRepairSnoozed) return null;

  return (
    <Box
      display={{ base: 'block', md: 'none' }}
      position="fixed"
      bottom={bottomOffset}
      left={3}
      right={3}
      zIndex={1200}
      bg="bgElevated"
      borderWidth="hairline"
      borderColor="border"
      borderRadius="xl"
      boxShadow="0 4px 24px rgba(0,0,0,0.5)"
      overflow="hidden"
      style={{ backdropFilter: 'blur(16px)' }}
      role="status"
      aria-live="polite"
    >
      <Box h="2px" bg="accent" />
      <Flex align="flex-start" gap={2.5} px={3} pt={3} pb={2}>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          justifyContent="center"
          w={7}
          h={7}
          flexShrink={0}
          borderRadius="md"
          color="textMuted"
          _hover={{ bg: 'whiteAlpha.100', color: 'text' }}
          cursor="pointer"
          onClick={dismissAndroidRepair}
          aria-label="Dismiss Android app repair tips"
        >
          <MdClose size={16} />
        </Box>

        <Box color="accent" flexShrink={0} mt={0.5}>
          <MdPhonelinkSetup size={22} />
        </Box>

        <VStack align="flex-start" gap={1.5} flex={1} minW={0}>
          <Heading fontWeight="semibold" fontSize="md" lineHeight={1.2}>
            {ANDROID_PWA_REPAIR_TITLE}
          </Heading>
          <Text fontSize="xs" color="textMuted">
            {ANDROID_PWA_REPAIR_SUMMARY}
          </Text>
          <Box as="ol" pl={4} fontSize="xs" color="text" display="flex" flexDirection="column" gap={1}>
            {ANDROID_PWA_REPAIR_STEPS.map((step) => (
              <Box as="li" key={step}>
                {step}
              </Box>
            ))}
          </Box>
          <Text
            fontSize="xs"
            color="accent"
            as={RouterLink}
            {...{ to: '/about#faq' } as Record<string, unknown>}
            onClick={dismissAndroidRepair}
          >
            More detail in the About FAQ
          </Text>
        </VStack>
      </Flex>
    </Box>
  );
}
